import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardBody, Col } from "reactstrap";
import TableContainer from "Components/Common/TableContainer";
import ActionMenu from "Components/Common/ActionMenu";
import { getAvatarStyle, getInitials } from "utils/getAvatarColor";
import { getStatusBadge } from "utils/getStatusBatch";
import { formatDate_DD_MMM_YYYY } from "utils/formatDates";
import { useAppDispatch, useAppSelector } from "Components/Hooks/hooks";
import {
  getPayments,
  regeneratePaymentLink,
  sharePayment,
  deletePayment,
} from "slices/thunks";
import { ErrorHandler } from "utils/toast";
import CustomShareModal from "Components/Modals/CustomShareModal";
import DeleteItemModal from "Components/Modals/DeleteItemModal";

const PaymentList = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [shareModalOpen, setShareModalOpen] = useState(false);
  const [deleteModal, setDeleteModal] = useState(false);
  const [selectedPayment, setSelectedPayment] = useState<any>(null);

  const { payments, pagination, status, generatedLinkDetails } = useAppSelector(
    (state) => state.payment,
  );

  useEffect(() => {
    dispatch(getPayments({ search: searchTerm, page: currentPage }));
  }, [dispatch, searchTerm, currentPage]);

  const handleSearch = useCallback((value: string) => {
    setSearchTerm(value);
    setCurrentPage(1);
  }, []);

  const handlePageChange = useCallback((page: number) => {
    setCurrentPage(page);
  }, []);

  const handleRegenerateLink = useCallback(
    async (rowData: any) => {
      try {
        setShareModalOpen(true);
        await dispatch(regeneratePaymentLink({ id: rowData.id })).unwrap();
      } catch (error) {
        ErrorHandler(error);
        setShareModalOpen(false);
      }
    },
    [dispatch],
  );

  const onClickDelete = useCallback((payment: any) => {
    setSelectedPayment(payment);
    setDeleteModal(true);
  }, []);

  const handleDeleteSelectedPayment = useCallback(async () => {
    if (selectedPayment) {
      try {
        await dispatch(deletePayment({ id: selectedPayment.id })).unwrap();
      } catch (error) {
        ErrorHandler(error);
      } finally {
        setDeleteModal(false);
        setSelectedPayment(null);
      }
    }
  }, [dispatch, selectedPayment]);

  const columns = useMemo(
    () => [
      {
        header: "ID",
        accessorKey: "id",
        enableColumnFilter: false,
        enableSorting: false,
        cell: (cell: any) => (
          <span className="fw-normal text-black2D fs-14 font-primary">
            {cell.getValue() || "-"}
          </span>
        ),
      },
      {
        header: "CLIENT",
        accessorKey: "client_name",
        enableColumnFilter: false,
        enableSorting: false,
        cell: (cell: any) => {
          const value = cell.getValue();
          return (
            <div className="d-flex align-items-center gap-2">
              <div className="avatar-circle" style={getAvatarStyle(value)}>
                {getInitials(value)}
              </div>
              <span className="fw-normal text-black2D fs-14 font-primary">
                {value || "-"}
              </span>
            </div>
          );
        },
      },
      {
        header: "TYPE",
        accessorKey: "amount_type",
        enableColumnFilter: false,
        enableSorting: false,
        cell: (cell: any) => (
          <span className="fw-normal text-black2D fs-14 font-primary">
            {cell.getValue() || "-"}
          </span>
        ),
      },
      {
        header: "AMOUNT",
        accessorKey: "amount_formatted",
        enableColumnFilter: false,
        enableSorting: false,
        cell: (cell: any) => (
          <span className="fw-normal text-black2D fs-14 font-primary">
            {`£${cell.getValue()}`}
          </span>
        ),
      },
      {
        header: "ALLOCATED",
        accessorKey: "allocated",
        enableColumnFilter: false,
        enableSorting: false,
        cell: (cell: any) => (
          <span className="fw-normal text-black2D fs-14 font-primary">
            {`£${cell.getValue()}`}
          </span>
        ),
      },
      {
        header: "CREDIT",
        accessorKey: "credit",
        enableColumnFilter: false,
        enableSorting: false,
        cell: (cell: any) => (
          <span className="fw-normal text-black2D fs-14 font-primary">
            {`£${cell.getValue()}`}
          </span>
        ),
      },
      {
        header: "DATE",
        accessorKey: "payment_date",
        enableColumnFilter: false,
        enableSorting: false,
        cell: (cell: any) => (
          <span className="fw-normal text-black2D fs-14 font-primary">
            {formatDate_DD_MMM_YYYY(cell.getValue())}
          </span>
        ),
      },
      {
        header: "STATUS",
        accessorKey: "status",
        enableColumnFilter: false,
        enableSorting: false,
        cell: (cell: any) => getStatusBadge(cell.getValue()),
      },
      {
        header: "METHOD",
        accessorKey: "payment_method",
        enableColumnFilter: false,
        enableSorting: false,
        cell: (cell: any) => (
          <span className="fw-normal text-black2D fs-14 font-primary">
            {cell.getValue()?.toString().charAt(0).toUpperCase() +
              cell.getValue()?.toString().slice(1) || "-"}
          </span>
        ),
      },
      {
        header: "ACTION",
        cell: (cell: any) => {
          const rowData = cell.row.original;
          return (
            <ActionMenu
              isView
              onView={() =>
                navigate("/view-payment", { state: { id: rowData.id } })
              }
              isDelete={rowData.status === "pending" || rowData.status === "failed" ? true : false}
              onDelete={() => onClickDelete(rowData)}
              isShare={
                rowData.status === "pending" 
                  ? true
                  : false
              }
              onShare={() => handleRegenerateLink(rowData)}
              isEdit={false}
            />
          );
        },
      },
    ],
    [navigate, handleRegenerateLink, onClickDelete],
  );

  return (
    <>
      <DeleteItemModal
        show={deleteModal}
        onDeleteClick={handleDeleteSelectedPayment}
        onCloseClick={() => {
          setDeleteModal(false);
          setSelectedPayment(null);
        }}
        disabled={status.delete === "loading"}
        isLoading={status.delete === "loading"}
        title="Delete Payment Record"
      />
      <Col xxl={12}>
        <Card className="br-10 bg-white">
          <CardBody className="pt-0">
            <TableContainer
              columns={columns}
              data={payments}
              isGlobalFilter={true}
              customPageSize={pagination?.per_page}
              divClass="table-responsive table-card mb-2 bg-white"
              tableClass="align-middle table-nowrap table-borderless"
              thClass="text-grey89 fw-medium fs-14 font-primary bg-greyF5"
              theadClass="table-light"
              SearchPlaceholder="Search payment id or clients"
              onSearch={handleSearch}
              onPageChange={handlePageChange}
              currentPage={pagination?.current_page}
              totalPages={pagination?.last_page}
              toPage={pagination?.to}
              fromPage={pagination?.from}
              totalItems={pagination?.total}
              isInitialLoading={status.fetch === "loading" && !payments?.length}
              isPaginationLoading={
                status.fetch === "loading" && payments?.length > 0
              }
            />
          </CardBody>
        </Card>
      </Col>
      <CustomShareModal
        isOpen={shareModalOpen}
        onClose={() => setShareModalOpen(false)}
        title="Share Payment Link"
        shareUrl={generatedLinkDetails?.stripe_payment_link_url ?? ""}
        subject="Payment Link"
        isLoading={status.regenerateLink === "loading"}
        recipientEmail={generatedLinkDetails?.client_email ?? ""}
        message={
          generatedLinkDetails?.client_name
            ? `Hi ${generatedLinkDetails.client_name}, here is your payment link:\n\n${generatedLinkDetails.stripe_payment_link_url}\n\nThank you.`
            : `Here is your payment link:\n\n${generatedLinkDetails?.stripe_payment_link_url ?? ""
            }\n\nThank you.`
        }
        onSendEmail={() =>
          generatedLinkDetails?.id &&
          dispatch(sharePayment({ id: generatedLinkDetails.id }))
        }
        isSending={status.share === "loading"}
      />
    </>
  );
};

export default PaymentList;
